import { Playable, PlayableList, Schedule, SCHEDULE_DAYS } from './types'
import { now as clockNow } from './clock'
import { Emitter } from './events'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

const DEFAULT_PRELOAD_INTERVAL = 8_000 // 8 seconds

export interface PlayScheduleItem {
  itemIndex: number
  startDate: Dayjs
  endDate: Dayjs
}

export type PlayScheduleItemWithSeek = PlayScheduleItem & { seek: number }

type PlaySchedule = PlayScheduleItem[]

export enum SCHEDULER_EVENTS {
  PLAY_START = 'play-start',
  PLAY_END = 'play-end',
}

export interface DateRange {
  start: Dayjs
  end: Dayjs
}

interface HourMinute {
  hour: number
  minute: number
  value: number
}

interface HourMinuteStartEnd {
  start: HourMinute
  end: HourMinute
}

export function createPreloadSchedule(
  playSchedule: PlaySchedule,
  preloadInterval = DEFAULT_PRELOAD_INTERVAL
): PlaySchedule {
  // ignore the first item, assuming it's already playing
  return playSchedule.slice(1).map((item) => {
    return {
      ...item,
      startDate: item.startDate.clone().subtract(preloadInterval, 'ms'),
      endDate: item.startDate.clone(),
    }
  })
}

// take a list of daily schedules, optimize and merge overlapping schedules
// ex: [10:00-16:35, 11:00-13:15, 14:00-17:00, 18:10-20:15] -> 10:00-17:00, 18:10-20:15
export function normalizeDailySchedule(dailyScheduleList: Array<{ start: string; end: string }>): HourMinuteStartEnd[] {
  if (!dailyScheduleList.length) return []

  function parseDailySchedule(dailySchedule: { start: string; end: string }) {
    function parseHourMinute(hourAndMinute: string): HourMinute {
      const [hour, minute] = hourAndMinute.split(':').map((x) => parseInt(x))
      const value = hour * 60 + minute // used in calculations - minutes since midnight
      return { hour, minute, value }
    }
    const { start, end } = dailySchedule
    return {
      start: parseHourMinute(start),
      end: parseHourMinute(end),
    }
  }

  const parsedScheduleList = dailyScheduleList.map(parseDailySchedule)
  const validSchedules = parsedScheduleList
    .filter((a) => a.start.value < a.end.value) // keep only valid schedules (start < end)
    .sort((a, b) => a.start.value - b.start.value) // sort by start time

  if (!validSchedules.length) return []

  const [first, ...rest] = validSchedules
  const mergedSchedule = [first]

  // merge overlapping schedules
  for (const schedule of rest) {
    const last = mergedSchedule[mergedSchedule.length - 1]
    // no overlap, add the new schedule
    if (schedule.start.value > last.end.value) {
      mergedSchedule.push(schedule)
    } else if (schedule.end.value > last.end.value) {
      last.end = schedule.end
    }
  }

  return mergedSchedule
}

// For the given date range, calculate date ranges where each playable is active
export function calcActiveSlots(
  playables: PlayableList,
  rangeBeginDate: Dayjs,
  rangeEndDate: Dayjs
): Array<DateRange[] | null> {
  const cache = new WeakMap<object, DateRange[]>()

  // Given a date range and a schedule, return the ranges where the schedule is active
  function calcActiveSlotsForSchedule(
    rangeBeginDate: Dayjs,
    rangeEndDate: Dayjs,
    schedule?: Schedule
  ): DateRange[] | null {
    if (!schedule || !schedule.schedules.length) return null // the playable is always active
    if (rangeEndDate < rangeBeginDate) return []

    if (cache.has(schedule)) {
      return cache.get(schedule)!
    }

    const dayDiff = rangeEndDate.diff(rangeBeginDate, 'days')
    const dayIndices = Array.from({ length: dayDiff + 1 }, (_, i) => i)

    const ranges: DateRange[] = []

    for (const dayIndex of dayIndices) {
      const isFirstDay = dayIndex === 0
      const isLastDay = dayIndex === dayDiff

      let currentDateBegin = rangeBeginDate.add(dayIndex, 'days')
      if (!isFirstDay) {
        currentDateBegin = currentDateBegin.startOf('day')
      }

      const day = SCHEDULE_DAYS[currentDateBegin.day()]
      const dailySchedulesRaw = schedule.schedules.map((sch) => sch.weekly[day]).filter((x) => !!x)

      // parse and merge overlapping schedules
      const dailySchedules = normalizeDailySchedule(dailySchedulesRaw)

      if (!dailySchedules.length) {
        continue
      }

      const rangesForDay: DateRange[] = []
      for (const dailySchedule of dailySchedules) {
        const {
          start: { hour: startHour, minute: startMinute },
          end: { hour: endHour, minute: endMinute },
        } = dailySchedule

        let startDateToday = currentDateBegin.set('hour', startHour).set('minute', startMinute)
        let endDateToday = currentDateBegin
          .set('hour', endHour)
          .set('minute', endMinute)
          .set('second', 0)
          .set('millisecond', 0)

        if (isFirstDay) {
          if (endDateToday < rangeBeginDate) continue
          if (startDateToday < rangeBeginDate) {
            startDateToday = rangeBeginDate
          }
        } else if (isLastDay) {
          if (startDateToday > rangeEndDate) continue
          if (endDateToday > rangeEndDate) {
            endDateToday = rangeEndDate
          }
        }

        rangesForDay.push({ start: startDateToday, end: endDateToday })
      }

      ranges.push(...rangesForDay)
    }

    return ranges
  }

  return playables.map((playable) => calcActiveSlotsForSchedule(rangeBeginDate, rangeEndDate, playable.schedule))
}

function calcPlayTime(playable: Playable, fromDate: Dayjs, activeSlots: DateRange[] | null): number {
  const durationInMs = playable.duration * 1000
  if (!activeSlots) return durationInMs
  const activeSlot = activeSlots.find((slot) => slot.start <= fromDate && slot.end > fromDate)
  if (!activeSlot) return 0
  const slotTimeRemaining = activeSlot.end.diff(fromDate, 'ms')

  return Math.min(durationInMs, slotTimeRemaining) // return duration unless it exceeds the slot duration
}

export class Scheduler {
  constructor(
    private playables: PlayableList,
    private timezone?: string
  ) {
    if (!this.timezone) {
      this.timezone = dayjs.tz.guess()
    }
  }

  init(syncronized = false): PlaySchedule {
    const findSyncronizationPoint = () => {
      // find midnight in UTC for the day before the current day
      // since we restart the player once a day, we can use this as a sync point
      const utcMidnight = dayjs(clockNow()).utc().startOf('day').subtract(1, 'day')
      return utcMidnight
    }
    const startDate = syncronized ? findSyncronizationPoint() : dayjs(clockNow())
    const endDate = dayjs(clockNow()).add(1, 'day')

    let playSchedule = this.createPlaySchedule(startDate, endDate)

    if (syncronized) {
      const startedAt = dayjs(clockNow())
      const indexForFirstItemNow = playSchedule.findIndex(
        (item) => item.startDate <= startedAt && item.endDate > startedAt
      )
      // remove schedules before now to save memory
      if (indexForFirstItemNow !== -1) {
        playSchedule = playSchedule.slice(indexForFirstItemNow)
      }
    }

    return playSchedule
  }

  public createPlaySchedule(startDate = dayjs(clockNow()), endDate?: Dayjs): PlaySchedule {
    const playables = this.playables
    if (playables.length === 0) return []

    // create schedule for the next 24 hours if endDate is not provided
    endDate = endDate ?? startDate.add(1, 'day')

    if (playables.length === 1 && (!playables[0].schedule || !playables[0].schedule.schedules.length)) {
      return [{ itemIndex: 0, startDate, endDate }]
    }

    const scheduleSlots = calcActiveSlots(playables, startDate, endDate)

    const playSchedule: PlaySchedule = []
    let endOfIteration = startDate.clone()

    let prevItem: PlayScheduleItem | null = null

    while (endOfIteration < endDate) {
      const start = endOfIteration.clone()
      for (let i = 0; i < playables.length; i++) {
        const startOfIteration = endOfIteration.clone()

        const durationInMs = calcPlayTime(playables[i], dayjs(startOfIteration, this.timezone), scheduleSlots[i])

        if (!durationInMs) continue

        endOfIteration = endOfIteration.add(durationInMs, 'ms')

        const itemToAdd = {
          itemIndex: i,
          startDate: startOfIteration,
          endDate: endOfIteration.clone(),
        }

        // merge consecutive slots with the same itemIndex and continuous time
        if (prevItem && prevItem.itemIndex === itemToAdd.itemIndex && prevItem.endDate.isSame(itemToAdd.startDate)) {
          prevItem.endDate = itemToAdd.endDate
        } else {
          playSchedule.push(itemToAdd)
          prevItem = itemToAdd
        }

        if (endOfIteration >= endDate) {
          break
        }
      }

      if (start.isSame(endOfIteration)) {
        endOfIteration = endOfIteration.add(1000, 'ms')
      }
    }

    return playSchedule
  }
}

const DEFAULT_SEEK_DELTA = 100

export class ScheduleNotifier extends Emitter {
  private currentItem: PlayScheduleItem | null = null
  private playTimer: NodeJS.Timeout | null = null

  constructor(private playSchedule: PlaySchedule) {
    super()
  }

  start(now = dayjs(clockNow())): void {
    this.scheduleNext(now)
  }

  stop(): void {
    if (this.playTimer) {
      clearTimeout(this.playTimer)
      this.playTimer = null
    }
    this.currentItem = null
  }

  destroy(): void {
    this.stop()
    this.removeAllListeners()
  }

  private calculateSeek(now: Dayjs, item: PlayScheduleItem): number {
    // Round down to zero if it is less than DEFAULT_SEEK_DELTA
    return Math.max(now.diff(item.startDate, 'ms') - DEFAULT_SEEK_DELTA, 0)
  }

  private scheduleNext(now = dayjs(clockNow())): void {
    const resetPlay = () => {
      if (this.currentItem) {
        this.currentItem = null
        this.emit(SCHEDULER_EVENTS.PLAY_END)
      }
      if (this.playTimer) {
        clearTimeout(this.playTimer)
        this.playTimer = null
      }
    }

    const nearestItemIndex = this.playSchedule.findIndex((item) => item.endDate > now)
    const nearestItem = this.playSchedule[nearestItemIndex]

    if (!nearestItem) {
      resetPlay()
      return
    }

    const timeToNextItemBegin = nearestItem.startDate.diff(now, 'ms')
    let nextCallInterval = timeToNextItemBegin

    if (nextCallInterval > 0) {
      resetPlay()
    } else {
      // next call will be at the end of the current item
      nextCallInterval = nearestItem.endDate.diff(now, 'ms')
      this.currentItem = nearestItem

      this.emit(SCHEDULER_EVENTS.PLAY_START, {
        ...this.currentItem,
        seek: this.calculateSeek(now, this.currentItem),
      })
    }

    this.playTimer = setTimeout(() => {
      this.scheduleNext()
    }, nextCallInterval)
  }
}
